import { useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import { AlertCircle, CheckCircle2, Database, Loader2, RefreshCw } from 'lucide-react';
import { CoproRegistryImportStatusDto } from 'api/referential';
import { referentialsApi } from 'services/swagger-api';
import { SimpleDashboardContent } from 'features/dashboard/DashboardContent';
import { GenericViewHeader } from 'back-office/shared/components/GenericViewHeader';
import { BlockCard } from 'back-office/shared/components/BlockCard';
import { useAsyncResource } from 'back-office/shared/hooks/useAsyncResource';

/** Cadence de rafraîchissement du statut pendant qu'un import tourne. */
const POLL_INTERVAL_MS = 5000;

const formatDate = (date?: Date | null) =>
  date
    ? new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long', timeStyle: 'short' }).format(new Date(date))
    : '—';

const formatCount = (count?: number) =>
  count != null ? new Intl.NumberFormat('fr-FR').format(count) : '—';

/**
 * Écran « Catalogue › Registre des copropriétés » : état du registre national (RNIC)
 * importé en base, et déclenchement de sa mise à jour.
 *
 * L'import télécharge le CSV quotidien de l'Anah depuis l'URL stable data.gouv et
 * bascule atomiquement la nouvelle génération : la recherche du funnel MRI n'est
 * jamais servie par un import partiel. Pas de fichier à fournir.
 */
export default function CoproRegistryView() {
  const fetchStatus = useCallback(() => referentialsApi.getCoproRegistryImportStatus(), []);
  const { data, isLoading, error, reload } = useAsyncResource<CoproRegistryImportStatusDto>(fetchStatus);

  const importRunning = data?.importRunning === true;

  // Suivi du job en cours : le statut se rafraîchit tout seul jusqu'à la fin de l'import.
  useEffect(() => {
    if (!importRunning) {
      return;
    }

    const interval = setInterval(() => {
      void reload();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [importRunning, reload]);

  const launchImport = async () => {
    try {
      await referentialsApi.launchCoproRegistryImport();
      toast.success('Mise à jour du registre lancée — le téléchargement et l’import prennent plusieurs minutes.');
      await reload();
    } catch {
      toast.error('Le lancement de la mise à jour a échoué.');
    }
  };

  const statusBadge = () => {
    if (importRunning) {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700">
          <Loader2 className="h-4 w-4 animate-spin" />
          Import en cours…
        </span>
      );
    }

    if (data?.lastImportStatus === 'Echec') {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1 text-sm font-medium text-red-700">
          <AlertCircle className="h-4 w-4" />
          Dernier import en échec
        </span>
      );
    }

    if (data?.lastImportStatus === 'Succes') {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1 text-sm font-medium text-green-700">
          <CheckCircle2 className="h-4 w-4" />
          Registre à jour
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-600">
        Aucun import effectué
      </span>
    );
  };

  return (
    <SimpleDashboardContent>
      <GenericViewHeader title="Registre des copropriétés" icon={Database} />

      <BlockCard
        title="Registre national (RNIC)"
        description="Les ~650 000 copropriétés immatriculées, publiées quotidiennement par l’Anah sur data.gouv. Le funnel MRI s’en sert pour le pré-remplissage syndic et la recherche sur carte."
        icon={<Database className="h-5 w-5" />}
        isLoading={isLoading && !data}
        error={error}
        headerExtra={statusBadge()}
      >
        <div className="flex flex-col gap-6">
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <dt className="text-sm text-gray-500">Copropriétés en base</dt>
              <dd className="mt-1 text-2xl font-semibold text-gray-900">{formatCount(data?.activeRowCount)}</dd>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <dt className="text-sm text-gray-500">Dernier import</dt>
              <dd className="mt-1 text-lg font-medium text-gray-900">{formatDate(data?.lastImportDate)}</dd>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <dt className="text-sm text-gray-500">Source</dt>
              <dd className="mt-1 text-lg font-medium text-gray-900">data.gouv — CSV quotidien</dd>
            </div>
          </dl>

          {data?.lastImportStatus === 'Echec' && data?.lastImportError && (
            <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{data.lastImportError}</span>
            </div>
          )}

          <div className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 p-4">
            <p className="text-sm text-gray-600">
              La mise à jour télécharge automatiquement le fichier du jour puis remplace le registre en une bascule
              atomique — la recherche reste disponible pendant toute l’opération.
            </p>
            <button
              type="button"
              onClick={launchImport}
              disabled={importRunning}
              className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${importRunning ? 'animate-spin' : ''}`} />
              {importRunning ? 'Mise à jour en cours…' : 'Mettre à jour le registre'}
            </button>
          </div>
        </div>
      </BlockCard>
    </SimpleDashboardContent>
  );
}
